import { useLogin } from '@/hooks';
import { useTypedNavigate } from '@/hooks/useTypedNavigate';
import { useMeQuery } from '@/remote/user-management/fetch-me.ts';
import { ROUTES } from '@/router/routes';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NavigationDrawer } from '../drawers';
import { PasswordChangeModal, ProfileEditModal } from '../modals';
import type { ToggleItem } from '../modules';
import { Toggle } from '../modules';

export default function Header() {
  const navigate = useTypedNavigate();
  const location = useLocation();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [navigationDrawerOpen, setNavigationDrawerOpen] = useState(false);

  const { logout } = useLogin();

  const handleLogout = async () => {
    setUserMenuOpen(false); // 메뉴 닫기
    await logout();
  };

  const { data: me } = useMeQuery();

  const userMenuItems: ToggleItem[] = [
    {
      id: 'edit-profile',
      label: '내 정보 수정',
      icon: 'ri-edit-line',
      onClick: () => setEditProfileOpen(true),
    },
    {
      id: 'change-password',
      label: '비밀번호 변경',
      icon: 'ri-lock-password-line',
      onClick: () => setChangePasswordOpen(true),
    },
    {
      id: 'logout',
      label: '로그아웃',
      icon: 'ri-logout-box-line',
      onClick: handleLogout,
      variant: 'danger' as const,
      separator: true,
    },
  ];

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-8">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-lg">
                <i className="ri-water-flash-line text-white"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">SIENG</span>
            </div>
            <nav className="hidden md:flex space-x-6">
              <button
                onClick={() => navigate(ROUTES.SITE_INFORMATION)}
                className={`font-medium px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap ${
                  location.pathname === ROUTES.SITE_INFORMATION
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                현장 정보
              </button>
              {(me.userLevel === 'SUPER_ADMINISTRATOR' ||
                me.userLevel === 'HEADQUARTERS_MANAGER' ||
                me.userLevel === 'PUBLIC_OFFICE_MANAGER') && (
                <button
                  onClick={() => navigate(ROUTES.SITE_MANAGEMENT)}
                  className={`font-medium px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap ${
                    location.pathname === ROUTES.SITE_MANAGEMENT
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  현장 관리
                </button>
              )}
              {(me.userLevel === 'SUPER_ADMINISTRATOR' ||
                me.userLevel === 'HEADQUARTERS_MANAGER') && (
                <button
                  onClick={() => navigate(ROUTES.USER_MANAGEMENT)}
                  className={`font-medium px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap ${
                    location.pathname === ROUTES.USER_MANAGEMENT
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  회원 관리
                </button>
              )}
              {me.userLevel === 'SUPER_ADMINISTRATOR' &&
                me.loginId !== 'siengsu' && (
                  <button
                    onClick={() => navigate(ROUTES.RAW_DATA)}
                    className={`font-medium px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap ${
                      location.pathname === ROUTES.RAW_DATA
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    개발 데이터
                  </button>
                )}
            </nav>
          </div>
          <div className="flex items-center space-x-4">
            <div className="hidden md:block">
              <Toggle
                trigger={
                  <div className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-user-line"></i>
                    </div>
                    <span className="whitespace-nowrap">내 정보</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i
                        className={`ri-arrow-${
                          userMenuOpen ? 'up' : 'down'
                        }-s-line transition-transform duration-200`}
                      ></i>
                    </div>
                  </div>
                }
                items={userMenuItems}
                isOpen={userMenuOpen}
                onToggle={() => setUserMenuOpen(!userMenuOpen)}
                onClose={() => setUserMenuOpen(false)}
                position="center"
              />
            </div>
            <button
              onClick={() => setNavigationDrawerOpen(true)}
              className="p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer md:hidden"
              title="메뉴 열기"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-menu-line"></i>
              </div>
            </button>
          </div>
        </div>
      </div>
      <ProfileEditModal
        isOpen={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
      />
      <PasswordChangeModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
      <NavigationDrawer
        isOpen={navigationDrawerOpen}
        onClose={() => setNavigationDrawerOpen(false)}
      />
    </header>
  );
}
